<!-- An inline time zone choice for running text, e.g. the /2026 intro's "(Sydney dates and times
     ⌄)": a link-like button that opens the time zone menu (TimeZoneMenu, 2026-10-14): the zones
     the Program offers (selectedTimeZones.js getZoneOptions), then every other zone by region. A
     choice applies everywhere at once (the intro's dates, the banner, the Program, #register's
     link) and is remembered (chooseTimeZone). Added 2026-10-13. The place is the zone's city,
     London included ("London dates and times"; not "UK", 2026-10-14).
     Accessibility: TimeZoneMenu's menu button. The accessible name keeps the visible label first
     (WCAG 2.5.3): "Sydney dates and times: change time zone". After a pick, a polite live region
     reads the new dates once. -->
<template>
  <span class="timeZoneInline" :class="{isCompact}">
    <TimeZoneMenu
      :options="options"
      :selected="selected"
      :label="buttonName"
      :is-inline="!isCompact"
      @pick="pick">
      <template v-if="isCompact">
        <svg class="globe" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="9" />
          <path
            d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9
              c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
        </svg>
        Time zone: {{ label }}
      </template>
      <template v-else>{{ `${label} ${suffix}` }}</template
      ><svg class="chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M6 9l6 6 6-6" />
      </svg>
    </TimeZoneMenu>
    <span class="visuallyHidden" role="status" aria-live="polite">{{ announcement }}</span>
  </span>
</template>

<script setup>
import {computed, nextTick, ref} from "vue";
import history from "@/assets/history.json";
import useUserSafe from "@/composables/useUserSafe";
import useLocalEventDates, {getZoneCity} from "@/composables/useLocalEventDates";
import TimeZoneMenu from "@/components/TimeZoneMenu.vue";
import {
  chooseTimeZone, getPreferredTimeZone, getZoneOptions
} from "@/composables/selectedTimeZones";

// variant: "link" (default: running text, "Sydney dates and times ⌄", in the brand's green) or
// "compact" (an outlined dropdown, "🌐 Time zone: Sydney ⌄", on the Key Dates heading's row and
// the Program; Seán's spec and mock-up, 2026-10-29);
// suffix: the link variant's words after the city ("dates and times"; "local time" after a
// date alone, the Masterclass page, 2026-11-01);
// eventZone, year: the event's (default: the latest year's; the Program passes its year's).
// "change" (zone): after a pick, for a page with more to update (the Program: its tables, URL)
const props = defineProps({
  variant: {type: String, default: "link"},
  suffix: {type: String, default: "dates and times"},
  eventZone: {type: String, default: ""},
  year: {type: [String, Number], default: ""}
});
const emit = defineEmits(["change"]);

const LATEST_ZONE = history.years[history.latestYear];

const eventZone = computed(() => props.eventZone || LATEST_ZONE);
const {user} = useUserSafe();
const {masterclass, workshop, hoursRange} = useLocalEventDates();
const announcement = ref("");
const selected = computed(() => getPreferredTimeZone(user.value));
const label = computed(() => getZoneCity(selected.value));
const isCompact = computed(() => props.variant === "compact");
// the visible label is part of the name (WCAG 2.5.3)
const buttonName = computed(() => {
  if (isCompact.value) return `Time zone: ${label.value}, change time zone`;
  return `${label.value} ${props.suffix}: change time zone`;
});
const options = computed(() => {
  const year = props.year || history.latestYear;
  const zones = getZoneOptions({eventZone: eventZone.value, user: user.value, year});
  return zones.includes(selected.value) ? zones : [...zones, selected.value].sort();
});

// read once, after the new dates are shown: "Sydney dates and times: Masterclass 4 November, …"
async function announce() {
  await nextTick();
  announcement.value = `${label.value} ${props.suffix}: Masterclass ${masterclass.value}, ` +
    `Workshop ${workshop.value}, each day from ${hoursRange.value}.`;
}

function pick(zone) {
  const isChange = zone !== selected.value;
  chooseTimeZone(user.value, zone);
  emit("change", zone);
  if (isChange) announce();
}
</script>

<style scoped>
/* in running text, the label wraps with the text (Seán, 2026-10-22); the compact variant
   (an outlined control) keeps to one line */
.timeZoneInline {
  position: relative;
}
.timeZoneInline.isCompact {
  white-space: nowrap;
}
/* in running text, the brand's green, not a link's blue (Seán, 2026-10-29): on the light theme
   a dark green beside the links' dark blue; on the dark theme a lighter, softer green, the brand
   mixed with the text colour, beside their muted blue */
.timeZoneInline :deep(.trigger) {
  font: inherit;
  color: color-mix(in srgb, var(--brand-dark-color) 80%, black);
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
}
.darkMode .timeZoneInline:not(.isCompact) :deep(.trigger) {
  color: color-mix(in srgb, var(--brand-color) 55%, var(--text-color));
}
/* hovered, open or focused: a dropdown, not a link (Seán, 2026-10-29, as the Email editor marks
   its editable links): the label and caret in a rounded box of the brand's light version
   (global.css --second-*), on each line when it wraps; its padding kept at rest too (a sliver
   of space inside the brackets), so the text doesn't move and the box clears the brackets */
.timeZoneInline:not(.isCompact) :deep(.trigger) {
  padding: 0.1em 0.25em;
  border-radius: 4px;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
}
.timeZoneInline:not(.isCompact) :deep(.trigger:is(:hover, :focus-visible, [aria-expanded="true"])) {
  --box: color-mix(in srgb, var(--brand-color) var(--second-share), var(--second-base));
  --edge: color-mix(
    in srgb, var(--brand-color) var(--second-border-share), var(--second-border-base)
  );
  text-decoration: none;
  background: var(--box);
  box-shadow: 0 0 0 1px var(--edge);
}
/* keyboard focus: visible outline (WCAG 2.4.7) */
.timeZoneInline:not(.isCompact) :deep(.trigger:focus-visible) {
  outline: 2px solid var(--brand-color);
  outline-offset: 2px;
}
/* the caret: drawn, about twice the old "⌄" (Seán, 2026-10-29: too small), the text's colour */
.chevron {
  display: inline-block;
  width: 0.9em;
  height: 0.9em;
  margin-left: 0.15em;
  vertical-align: -0.12em;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}
/* compact: a globe and "Time zone: <city>" (Seán, 2026-10-29), in the brand's light version, a
   secondary control (DESIGN.md § 5: you can press this, not you must): global.css --second-*,
   on the light theme dark brand text on a pale brand fill, on the dark theme light brand text on
   dark grey, as /Videos' Purchase access; the brand's full colour on its border when open or
   focused, a touch darker on hover */
.isCompact :deep(.trigger) {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  font-size: 0.9rem;
  line-height: 1.4;
  padding: 0.3em 0.7em;
  color: color-mix(
    in srgb,
    white var(--second-text-lift),
    color-mix(in srgb, black var(--second-text-darken), var(--brand-color))
  );
  background: color-mix(in srgb, var(--brand-color) var(--second-share), var(--second-base));
  border: 1px solid
    color-mix(in srgb, var(--brand-color) var(--second-border-share), var(--second-border-base));
  border-radius: 6px;
}
.isCompact :deep(.trigger:focus-visible),
.isCompact :deep(.trigger[aria-expanded="true"]) {
  border-color: var(--brand-color);
  text-decoration: none;
}
.isCompact :deep(.trigger:hover) {
  filter: brightness(0.97);
  text-decoration: none;
}
.isCompact :deep(.trigger:focus-visible) {
  outline: 2px solid var(--brand-color);
  outline-offset: 2px;
}
.isCompact .chevron {
  width: 1em;
  height: 1em;
  margin-left: 0;
  flex: none;
}
.globe {
  width: 1.05em;
  height: 1.05em;
  flex: none;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
}

/* read by screen readers, not shown */
.visuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
</style>
